import { useInView } from '../../hooks/useInView'

function fadeUp(inView: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

// Метрики измеряют конверсию клика в активацию оффера (get_offer), не в покупку.
// Формулировки не сокращать до «конверсии в покупку» — это разные величины.
// Обе величины — доли от одного и того же знаменателя (клики), поэтому лежат
// на общей шкале 0–100% и показаны штрихами одной дуги.
const METRICS = [
  {
    pct: 44.93,
    value: '44,93%',
    title: 'В среднем по площадке',
    desc: 'Почти половина всех кликов по предложениям доходит до целевого действия.',
    color: '#69a6ff',
    // Заливка светлая: белый текст на ней даёт 2.47 при норме 4.5, поэтому
    // значение выносим за полосу и набираем чернилами (18.39). Затемнить саму
    // полосу нельзя — до нормы она доходит только к #0a6dff и становится
    // неотличима от второй полосы (#145aff), а график держится на их различии.
    valueOutside: true,
  },
  {
    pct: 22.08,
    value: '22,08%',
    title: 'В среднем по одной кампании',
    desc: 'Столько кликов доходит до целевого действия в типичной рекламной кампании.',
    color: '#145aff',
  },
]

const GRID = [0, 25, 50, 75, 100]

/** Полоса-пилюля: ширина = доля от всех кликов. */
function Bar({ m, inView, delay }: { m: (typeof METRICS)[number]; inView: boolean; delay: number }) {
  const fill = {
    width: inView ? `${m.pct}%` : '0%',
    transition: `width 1.1s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
  // Значение уезжает за полосу в двух случаях: у светлой заливки — всегда
  // (белым по ней не хватает контраста), у тёмной — только на узких экранах,
  // где короткая полоса 22% уже собственной подписи.
  const outsideAlways = 'valueOutside' in m && m.valueOutside
  return (
    <div>
      <span className="mb-2 block text-[15px] font-medium leading-5 text-[#27272a]">{m.title}</span>
      <div className="relative h-11">
        {/* Трек = 100% кликов */}
        <div className="absolute inset-0 rounded-full bg-[#eef1f6]" />
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ ...fill, background: m.color }}
        />
        <div
          className={`absolute inset-y-0 left-0 flex items-center${outsideAlways ? '' : ' sm:hidden'}`}
          style={fill}
        >
          <span
            className="font-display absolute left-full ml-2 whitespace-nowrap text-[15px] font-semibold text-[#041527] sm:text-[16px]"
          >
            {m.value}
          </span>
        </div>
        {!outsideAlways && (
          <div className="absolute inset-y-0 left-0 hidden items-center sm:flex" style={fill}>
            <span className="font-display pl-5 text-[16px] font-semibold text-white">{m.value}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function ConversionBars({ inView }: { inView: boolean }) {
  return (
    <div
      className="w-full"
      role="img"
      aria-label="Доля кликов, дошедших до целевого действия: в среднем по площадке 44,93 процента, в среднем по одной кампании 22,08 процента"
    >
      {/* Разметка шкалы */}
      <div className="relative mb-3 h-5">
        {GRID.map((g) => (
          <span
            key={g}
            className="font-body absolute top-0 text-[12px] leading-5 text-[#676b94]"
            style={{
              left: `${g}%`,
              transform: g === 100 ? 'translateX(-100%)' : g === 0 ? 'none' : 'translateX(-50%)',
            }}
          >
            {g}%
          </span>
        ))}
      </div>

      {/* Полосы на фоне вертикальной сетки */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          {GRID.map((g) => (
            <span
              key={g}
              className="absolute top-0 bottom-0 w-px bg-[#e9edf3]"
              style={{ left: `${g}%`, transform: g === 100 ? 'translateX(-100%)' : 'none' }}
            />
          ))}
        </div>

        <div className="relative flex flex-col gap-6">
          {METRICS.map((m, i) => (
            <Bar key={m.title} m={m} inView={inView} delay={200 + i * 180} />
          ))}
        </div>
      </div>

    </div>
  )
}

export function LeadQuality() {
  const { ref, inView } = useInView(0.4, '0px 0px -80px 0px')

  return (
    <section
      id="lead-quality"
      className="mx-auto flex max-w-[1920px] flex-col items-center gap-8 px-4 py-14 sm:gap-[42px] sm:px-6 sm:py-20 lg:px-[7%] lg:py-28"
    >
      {/* Header */}
      <div ref={ref} className="flex flex-col items-center gap-5 text-center" style={fadeUp(inView, 0)}>
        <span
          className="inline-block text-[12px] font-bold uppercase text-accent"
          style={{ letterSpacing: '2px', lineHeight: '24px', fontFamily: 'Helvetica, Arial, Inter, system-ui, sans-serif' }}
        >
          Модель оплаты
        </span>
        <h2 className="font-display text-[28px] max-[359px]:text-[24px] font-semibold leading-[1.14] text-[#041527] sm:text-[44px] lg:text-[56px] lg:leading-[64px]">
          Вы платите за&nbsp;результат,
          <br className="hidden sm:block" />
          {' '}а&nbsp;не&nbsp;за&nbsp;показы
        </h2>
        <p className="font-body max-w-[748px] text-[15px] leading-[23px] text-[#676b94] sm:text-[16px] sm:leading-[26px] lg:text-[20px] lg:leading-[30px]">
          Деньги списываются только тогда, когда пользователь дошёл до&nbsp;целевого действия.
        </p>
      </div>

      {/* График: полосы на общей шкале 0–100% (100% = все клики) + пояснения */}
      <div
        className="flex w-full max-w-[1000px] flex-col gap-10 rounded-[20px] border border-[#e4e4e7] bg-gradient-to-b from-white to-[#f4f4f5] px-6 py-10 sm:px-10 lg:py-12"
        style={fadeUp(inView, 100)}
      >
        <div className="flex flex-col gap-6">
          {/* Подзаголовок графика: без него не считывается, что за величину
              откладывают полосы и что принято за 100%. */}
          <div className="flex flex-col gap-1.5">
            <p className="text-[18px] font-medium leading-6 tracking-[-0.2px] text-[#27272a] sm:text-[22px] sm:leading-7">
              Доля кликов, которые доходят до&nbsp;целевого действия
            </p>
            <p className="font-body text-[14px] leading-5 text-[#676b94] sm:text-[15px] sm:leading-6">
              За&nbsp;100% взяты все клики по&nbsp;предложениям.
            </p>
          </div>

          <ConversionBars inView={inView} />
        </div>

        {/* Цвет метрики раньше читался по самой цифре, но светлая заливка #69a6ff
            даёт на белом 2.47 — не хватает даже до порога 3.0 для крупного текста.
            Поэтому цвет ушёл с текста на плашку-образец: цифра набрана чернилами
            (18.39), а связь с полосой выше держит плашка той же заливки.
            К плашке порог 3.0 не применяется — смысл несёт подпись рядом,
            цвет только дублирует уже написанное. */}
        <ul className="grid grid-cols-1 gap-7 border-t border-[#e9edf3] pt-8 md:grid-cols-2 md:gap-10">
          {METRICS.map((m) => (
            <li key={m.title}>
              <span className="font-display flex items-center gap-2.5 text-[30px] font-bold leading-none tracking-[-1px] text-[#041527] sm:text-[44px]">
                <span
                  aria-hidden
                  className="h-2.5 w-6 shrink-0 rounded-full sm:h-3 sm:w-8"
                  style={{ background: m.color }}
                />
                {m.value}
              </span>
              <p className="mt-2 text-[17px] font-medium leading-6 tracking-[-0.2px] text-[#27272a] sm:text-[20px] sm:leading-7">{m.title}</p>
              <p className="font-body mt-1.5 text-[15px] leading-[22px] text-[#52525b] sm:mt-2 sm:text-[16px] sm:leading-6">{m.desc}</p>
            </li>
          ))}
        </ul>
      </div>

    </section>
  )
}
